import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { ORDER_TYPES } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import styled from 'styled-components';
import { QuantityPicker } from '@components/quantity-picker';
import { OrderTimePicker } from '@components/order-settings-modal/order-time-picker';
import { breakpoints } from '../../../constants';
import { Box, Flex } from '../../grid';
import { Icon } from '../../icons';
import { P, pStyles } from '../../typography/new';
import { validateCateringPickupTime } from '../../../utils/validate';
import { TextInput } from '../../input/text-input';
import { getCateringMaxDaysFromToday, useCateringDateValidation } from '../../../hooks/use-catering-date-validation';
import { useCateringShortLeadModal } from '../../../hooks/use-catering-short-lead-modal';
import { ShortLeadTimeErrorModal } from '../short-lead-time-error-modal';

const SchedulePickUp = observer(({ restaurant, orderType, updateSetting, settings }) => {
  const [isTimeValid, setIsTimeValid] = useState(true);
  const [isDateValid, setIsDateValid] = useState(true);
  const [isTimeBlocked, setIsTimeBlocked] = useState(false);
  const [shortLeadDismissed, setShortLeadDismissed] = useState(false);
  const { OrderingContextStore, CartStore } = useRootStore();
  const { getMinDate, validateDate } = useCateringDateValidation();
  const isDelivery = orderType === ORDER_TYPES.CATERING_DELIVERY;

  // Derive selectedTime and headcount from the modal settings first, then fall back to store
  const cateringDate = settings?.cateringPickupDate || OrderingContextStore.cateringPickupDate;
  const cateringTime = settings?.cateringPickupTime || OrderingContextStore.cateringPickupTime;
  const cateringHeadcount = settings?.cateringHeadcount ?? OrderingContextStore.cateringHeadcount ?? 0;

  // The picker runs against the modal's local edit state, not the committed
  // OrderingContextStore — pass the local values into the shared hook so it
  // detects violations against in-flight edits.
  const { shortLeadModal, dismissShortLeadModal } = useCateringShortLeadModal({
    pickupDate: cateringDate,
    pickupTime: cateringTime,
    onDismiss: () => {
      updateSetting('cateringPickupDate', null);
      updateSetting('cateringPickupTime', null);
      setShortLeadDismissed(true);
    },
  });

  // Date filter logic for DatePicker
  const subtotalCents = CartStore.subtotalPrices?.cents || 0;

  const filterDate = useCallback(
    date => {
      if (!restaurant?.cateringWindow) {
        return false;
      }

      const today = moment().startOf('day');
      const mDate = moment(date).startOf('day');
      const todayDayIndex = today.day(); // Use TODAY's day index, not the target day
      const config = restaurant.cateringWindow.find(cfg => cfg.dayIndex === todayDayIndex);

      if (!config) {
        return false;
      }

      const daysFromToday = mDate.diff(today, 'days');
      let effectiveStartsInDays = config.cateringPickupWindowStartsInDays;

      // If same-day is configured but the cutoff has passed, push the earliest
      // selectable day to tomorrow so the picker never lands on an unfillable today.
      if (
        effectiveStartsInDays === 0 &&
        typeof restaurant.isSameDayCateringPastCutoff === 'function' &&
        restaurant.isSameDayCateringPastCutoff(today.toDate())
      ) {
        effectiveStartsInDays = 1;
      }

      // If cart exceeds the per-day cap, the short-lead override no longer applies —
      // fall back to the restaurant's standard lead time for today.
      const standardHours = config.cateringStandardLeadTimeHours;
      if (standardHours != null) {
        const standardDays = Math.ceil(standardHours / 24);
        const maxValue = config.cateringPickupWindowMaxOrderValue ?? restaurant.cateringPickupWindowMaxOrderValueGlobal;
        const subtotalDollars = subtotalCents / 100;
        if (maxValue != null && subtotalDollars >= maxValue && effectiveStartsInDays < standardDays) {
          effectiveStartsInDays = standardDays;
        }
      }

      const maxDaysFromToday = getCateringMaxDaysFromToday(
        { startsInDays: effectiveStartsInDays, durationDays: config.cateringPickupWindowDurationDays },
        orderType
      );

      const isValid = daysFromToday >= effectiveStartsInDays && daysFromToday <= maxDaysFromToday;

      return isValid;
    },
    [restaurant?.cateringWindow, restaurant, subtotalCents, orderType]
  );

  // Get the first available date that passes the filter
  const getFirstAvailableDate = useCallback(() => {
    if (!restaurant?.cateringWindow) return null;

    const today = moment().startOf('day');
    // Check up to 30 days from today to find the first available date
    for (let daysFromToday = 0; daysFromToday < 30; daysFromToday += 1) {
      const checkDate = moment(today)
        .add(daysFromToday, 'days')
        .toDate();
      if (filterDate(checkDate)) {
        return checkDate;
      }
    }
    return null; // No available date found within 30 days
  }, [restaurant?.cateringWindow, filterDate]);

  // Memoize selectedDate to prevent creating new Date objects on every render
  const selectedDate = useMemo(() => {
    if (cateringDate) {
      return moment(cateringDate).toDate();
    }
    return getFirstAvailableDate();
  }, [cateringDate, getFirstAvailableDate]);

  // Memoize availablePickupTimesWithBlockSlots to avoid MobX re-render loop
  const availablePickupTimesWithBlockSlots = useMemo(
    () => (restaurant ? restaurant.availablePickupTimesWithBlockSlots({ orderType, date: selectedDate }) : []),
    [restaurant, orderType, selectedDate]
  );

  const isStoredTimeAvailable = !!(
    cateringTime && availablePickupTimesWithBlockSlots.some(slot => slot.value === cateringTime && !slot.disabled)
  );
  const firstAvailableTimeValue = availablePickupTimesWithBlockSlots.find(slot => !slot.disabled)?.value || '';
  const selectedTime = isStoredTimeAvailable ? cateringTime : firstAvailableTimeValue;

  // Set defaults when restaurant changes or when values are missing
  useEffect(() => {
    if (!restaurant?.cateringWindow) return;

    if (!cateringDate && !shortLeadModal && !shortLeadDismissed) {
      const defaultDate = getFirstAvailableDate();
      if (defaultDate) {
        const dateString = moment(defaultDate).format('YYYY-MM-DD');
        updateSetting('cateringPickupDate', dateString);
        setIsDateValid(true);
      } else {
        setIsDateValid(false);
      }
    } else if (cateringDate) {
      // CHECK: If we have a stored date, validate it's still valid
      const storedDate = moment(cateringDate).toDate();
      const isStoredDateValid = validateDate(storedDate, restaurant, subtotalCents, orderType);

      if (!isStoredDateValid) {
        setIsDateValid(false);
      } else {
        // Stored date is valid, ensure error state is cleared
        setIsDateValid(true);
      }
    }

    if ((!cateringTime || !isStoredTimeAvailable) && availablePickupTimesWithBlockSlots.length > 0) {
      const firstAvailableTime = availablePickupTimesWithBlockSlots.find(slot => !slot.disabled);
      if (firstAvailableTime && firstAvailableTime.value !== cateringTime) {
        updateSetting('cateringPickupTime', firstAvailableTime.value);
      }
    }

    if (typeof cateringHeadcount !== 'number') {
      updateSetting('cateringHeadcount', 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    restaurant?.id,
    orderType,
    cateringDate,
    cateringTime,
    cateringHeadcount,
    availablePickupTimesWithBlockSlots.length,
  ]);

  // Check if the selected time is blocked
  useEffect(() => {
    if (!cateringTime || !availablePickupTimesWithBlockSlots.length) {
      setIsTimeBlocked(false);
      return;
    }

    const selectedTimeSlot = availablePickupTimesWithBlockSlots.find(slot => slot.value === cateringTime);
    const isBlocked = selectedTimeSlot?.disabled || false;
    setIsTimeBlocked(isBlocked);
  }, [cateringTime, availablePickupTimesWithBlockSlots]);

  const onTimeChange = newTime => {
    const isValidTime = validateCateringPickupTime(newTime);
    setIsTimeValid(isValidTime);
    if (!isValidTime) return;

    // Update only the time part using updateSetting
    if (cateringTime !== newTime) {
      updateSetting('cateringPickupTime', newTime);
    }
  };

  const onHeadcountChange = newHeadcount => {
    if (cateringHeadcount !== newHeadcount) {
      updateSetting('cateringHeadcount', newHeadcount);
    }
  };

  const onDateChange = date => {
    if (date) {
      // User is actively engaging with the picker — re-enable short-lead checks.
      setShortLeadDismissed(false);

      // Validate the selected date
      if (!validateDate(date, restaurant, subtotalCents, orderType)) {
        setIsDateValid(false);
        return;
      }

      setIsDateValid(true);

      // Convert date to YYYY-MM-DD format for storage
      const dateString = moment(date).format('YYYY-MM-DD');
      if (cateringDate !== dateString) {
        updateSetting('cateringPickupDate', dateString);
      }
    }
  };

  const onFocusChange = event => {
    const newTime = event.target.value;
    onTimeChange(newTime);
  };

  const formattedStatusMessage = useMemo(
    () => (restaurant ? restaurant.getFormattedStatusMessage({ orderType }) : []),
    [restaurant, orderType]
  );
  const disabled = formattedStatusMessage?.disableOptions || false;
  if (disabled) return null;

  // Custom input for DatePicker using TextInput
  const DateTextInput = React.forwardRef(({ value, onClick, placeholder, errorMessage, isError, name }, ref) => {
    // Format the date value for display (DatePicker passes Date object, but TextInput needs string)
    const formattedValue = value instanceof Date ? moment(value).format('DD/MM/YYYY') : value || '';

    return (
      <TextInput
        name={name}
        value={formattedValue}
        onClick={onClick}
        placeholder={placeholder}
        errorMessage={errorMessage}
        isError={isError}
        readOnly
        ref={ref}
        testId="order-date-picker"
        width="100%"
        iconLeft={<Icon mr={1} name="schedule" />}
        inputProps={{ onClick, ref }} // Ensure onClick and ref are passed to the actual input
      />
    );
  });
  DateTextInput.propTypes = {
    value: PropTypes.string,
    onClick: PropTypes.func,
    placeholder: PropTypes.string,
    errorMessage: PropTypes.string,
    isError: PropTypes.bool,
    name: PropTypes.string,
  };
  DateTextInput.defaultProps = {
    value: '',
    onClick: undefined,
    placeholder: '',
    errorMessage: '',
    isError: false,
    name: '',
  };

  const MyContainer = ({ children }) => {
    return (
      <Flex
        flexDirection="column"
        position="relative"
        border="1px solid"
        borderColor="grey73"
        style={{ padding: '16px 24px 20px', backgroundColor: 'white', color: '#fff' }}
      >
        <div id="calender-content">{children}</div>
        <Box width="280px" mt="8px">
          <P variant="extra-small" color="grey50">
            • We require at least 24hrs notice Mon-Thurs, or 48hrs Fri-Sun (orders before 3PM)
          </P>
          <P variant="extra-small" color="grey50">
            • Minimum ${restaurant?.minimumCateringOrderSpend} order
          </P>
        </Box>
      </Flex>
    );
  };
  MyContainer.propTypes = {
    children: PropTypes.node,
  };
  MyContainer.defaultProps = {
    children: null,
  };

  return (
    <>
      <Flex flexDirection="column" gap={{ xs: '12px', lg: '24px' }}>
        <Flex flexDirection="column">
          <P as="label" mb="12px" display="block" variant="small-semi-bold" htmlFor="pickupDate">
            Date
          </P>
          <Flex justifyContent="center" alignItems="center">
            <Box>
              <Icon mr="16px" name="schedule" />
            </Box>
            <StyledDatePickerWrapper>
              <StyledDatePicker
                selected={selectedDate}
                onChange={onDateChange}
                minDate={getMinDate(restaurant, subtotalCents)}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select a date"
                id="pickupDate"
                popperPlacement="bottom-start"
                name="pickupDate"
                filterDate={filterDate}
                calendarContainer={MyContainer}
                customInput={
                  <DateTextInput
                    value={selectedDate}
                    name="pickupDate"
                    errorMessage="Please select a valid date."
                    isError={!isDateValid}
                    placeholder="Select a date"
                  />
                }
              />
            </StyledDatePickerWrapper>
          </Flex>
        </Flex>
        <Flex flexDirection="column">
          <P as="label" mb="12px" display="block" variant="small-semi-bold" htmlFor="pickupTime">
            When
          </P>

          <Flex justifyContent="center" alignItems="center">
            <Icon mr="16px" name="clock" />
            <Box width="100%">
              <OrderTimePicker
                errorMessage={isDelivery ? 'Please enter a valid delivery time' : 'Please enter a valid pickup time'}
                isError={!isTimeValid || isTimeBlocked}
                defaultValue={selectedTime}
                name="pickupTime"
                onChange={onTimeChange}
                onFocus={onFocusChange}
                options={availablePickupTimesWithBlockSlots}
                placeholder={isDelivery ? 'Select a delivery time' : 'Select a pickup time'}
              />
            </Box>
          </Flex>
        </Flex>
        <Flex flexDirection="column">
          <P as="label" mb="16px" display="block" variant="small-semi-bold" htmlFor="pickupTime">
            How many guests (optional)
          </P>

          <QuantityPicker
            height="44px"
            quantity={cateringHeadcount}
            increment={() => onHeadcountChange(cateringHeadcount + 1)}
            decrement={() => onHeadcountChange(cateringHeadcount - 1)}
            onQuantityChange={value => onHeadcountChange(value)}
            min={0}
            max={1000}
          />
        </Flex>
      </Flex>
      {shortLeadModal && (
        <ShortLeadTimeErrorModal
          open
          variant={shortLeadModal.variant}
          maxValue={shortLeadModal.maxValue}
          standardLeadHours={shortLeadModal.standardLeadHours}
          cutoffTime={shortLeadModal.cutoffTime}
          requestedTime={shortLeadModal.requestedTime}
          onClose={dismissShortLeadModal}
        />
      )}
    </>
  );
});

const StyledDatePickerWrapper = styled.div`
  flex: 1;
  position: relative;

  /* Override default react-datepicker styles */
  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container {
    width: 100%;
  }

  .react-datepicker-popper {
    top: -320px !important;
    transform: translate(0px) !important;
    left: -26px !important;
  }

  .react-datepicker {
    font-family: 'Barlow', sans-serif;
    border: 1px solid ${({ theme }) => theme.colors.grey300};
    border-radius: 6px;
    box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.1);
    background-color: ${({ theme }) => theme.colors.white};
  }

  .react-datepicker__header {
    background-color: ${({ theme }) => theme.colors.white};
    border-bottom: none;
    padding: 0px;
    margin: 0px;
  }

  .react-datepicker__current-month {
    ${pStyles('medium-bold')}
    color: ${({ theme }) => theme.colors.black};
    font-family: 'Barlow', sans-serif;
    margin-bottom: 12px;
    text-align: center;
  }

  .react-datepicker__day-names {
    display: flex;
    justify-content: space-between;
  }

  .react-datepicker__day-name {
    ${pStyles('small-semi-bold')}
    color: ${({ theme }) => theme.colors.black};
    font-family: 'Barlow', sans-serif;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
  }

  .react-datepicker__day {
    ${pStyles('standard')}
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colors.black};
    font-family: 'Barlow', sans-serif;
    border-radius: 6px;
    cursor: pointer;
    margin: 0;
    transition: background-color 0.2s ease;
    position: relative;
    overflow: visible;

    &:hover {
      background-color: ${({ theme }) => theme.colors.grey100};
    }

    &.react-datepicker__day--selected {
      background-color: ${({ theme }) => theme.colors.black};
      color: ${({ theme }) => theme.colors.white};
      ${pStyles('medium-bold')}
      border-radius: 5px;
    }

    &.react-datepicker__day--today {
      background-color: ${({ theme }) => theme.colors.black};
      color: ${({ theme }) => theme.colors.white};
      ${pStyles('medium-bold')}
      border-radius: 5px;
    }

    &.react-datepicker__day--disabled {
      opacity: 0.3;
      color: ${({ theme }) => theme.colors.grey300};
      cursor: not-allowed;

      &:hover {
        background-color: transparent;
      }
    }

    &.react-datepicker__day--outside-month {
      opacity: 0.3;
      color: ${({ theme }) => theme.colors.grey300};
    }
  }

  .react-datepicker__navigation-icon {
    &::before {
      border-color: ${({ theme }) => theme.colors.black};
      border-width: 2px 2px 0 0;
      width: 8px;
      height: 8px;
    }
  }

  .react-datepicker__navigation {
    width: 24px;
    top: 15px;
    border: none;
    background: none;

    &--previous {
      left: 32px;
    }

    &--next {
      right: 32px;
    }
  }

  @media (min-width: ${breakpoints.md}) {
    .react-datepicker__day-name {
      width: 40px;
      height: 40px;
    }

    .react-datepicker__day {
      width: 40px;
      height: 40px;
    }

    .react-datepicker__navigation {
      top: 17px;
    }

    .react-datepicker-popper {
    top: -300px !important;
    transform: translate(0px) !important;
    left: 0 !important;
  }
  }

  .react-datepicker__month {
    margin: 0;
  }

  .react-datepicker__week {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0px;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.grey300};
  border-radius: 2px;
  font-family: 'Barlow', sans-serif;
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.black};

  &:focus {
    border-color: ${({ theme }) => theme.colors.info400};
    box-shadow: 0px 0px 6px 0px ${({ theme }) => `rgba(${theme.colors.info400}, 0.25)`};
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey300};
  }
`;

export { SchedulePickUp };

SchedulePickUp.propTypes = {
  restaurant: PropTypes.object.isRequired,
  orderType: PropTypes.oneOf(Object.values(ORDER_TYPES)).isRequired,
  updateSetting: PropTypes.func.isRequired,
  settings: PropTypes.object,
};

SchedulePickUp.defaultProps = {
  settings: {},
};
